
import { Component, Prop, Vue } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';

import { sharedState } from '../../../../framework/state';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';
import { FileMeta } from '../../../../../common/application/model/FileMeta';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import TextField from '../../../../framework/fields/TextField.vue';
import TextAreaField from '../../../../framework/fields/TextAreaField.vue';
import { ValidationObserver } from 'vee-validate';

import { SortOrder } from '../../../../../common/framework/model/SortOrder';
import { dateToDbDateString } from '../../../../../common/framework/util/convert';
import { deleteFileS3, getFileS3, getPresignedUrlObjectS3 } from '../../../client/file_meta_client';
import { FrameworkUserRole } from '../../../../../common/framework/enumeration/FrameworkUserRole';
import { ApplicationUserRole } from '../../../../../common/application/enumeration/ApplicationUserRole';
import { errorToast, successToast } from '../../../service/toast_service';
import ParameterService from '../../../service/parameter_service';
import { P_PAGE_SIZE } from '../../../../../common/framework/constants';

@Component({
    components: {
        TextField,
        ValidationObserver,
        Multiselect,
        TextAreaField,
    },
})
export default class ViewDocuments extends Vue {
    // Properties
    @Prop(String) readonly resourceId!: string;
    @Prop(String) readonly resourceType!: ApplicationResource;
    parameterService = new ParameterService(ApplicationResource.FILE_META);

    // State
    shared = sharedState;
    local = {
        total: 0,
        loading: false,
        rows: [] as FileMeta[],
        parameters: this.parameterService.get(),
        showPreview: true,
    };

    // Functions
    async mounted() {
        this.local.loading = true;

        await this.loadAsyncData();

        this.local.loading = false;
    }

    async onSortChange(field: string, order: SortOrder) {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    async download(attachment: FileMeta) {
        const result = await getFileS3(this.resourceType, attachment.id, attachment.name);
        const aElement = document.createElement('a');
        aElement.setAttribute('download', attachment.name);
        const href = URL.createObjectURL(result);
        aElement.href = href;
        aElement.setAttribute('target', '_blank');
        aElement.click();
        URL.revokeObjectURL(href);
    }

    confirmDelete(attachment: FileMeta) {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                try {
                    const fileName = `${attachment.id}_${attachment.name}`;
                    await deleteResource(ApplicationResource.FILE_META, attachment.id);
                    await deleteFileS3(this.resourceType, fileName);
                    successToast(this, 'message.resourceDeleted');
                    await this.resetPagingAndLoadData();
                } catch (e) {
                    errorToast(this);
                }
            },
        });
    }

    dateToString(date: string | undefined): string {
        if (!date) {
            return '';
        }

        return dateToDbDateString(new Date(date));
    }

    formatBytes(bytes: number, decimals = 2) {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/file-meta/${id}/edit`);
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    async preview(attachment: FileMeta) {
        const fileName = `${attachment.id}_${attachment.name}`;
        const previewUrl = await getPresignedUrlObjectS3(this.resourceType, fileName);
        window.open(previewUrl.url, '_blank');
    }

    isAbleToEdit(userId: string) {
        if (this.shared.hasRole(ApplicationUserRole.MANAGER)) {
            return userId === this.shared.context.userId;
        } else if (this.isAdmin) {
            return true;
        }
    }

    private async loadAsyncData() {
        const backendParameters = this.parameterService.getBackendParameters();

        backendParameters.set(P_PAGE_SIZE, 100);
        backendParameters.set('resourceId', this.resourceId);

        this.local.rows = [];
        const rows = await getResourcesNew<FileMeta>(
            ApplicationResource.FILE_META,
            this.local.parameters.page,
            backendParameters,
        );

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (rows.length) {
            backendParameters.set(
                'id',
                rows.map(({ id }) => id),
            );
            this.local.total = (
                await getResourcePagingNew(ApplicationResource.FILE_META, backendParameters, undefined)
            ).rowCount;
        }
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
